import React, { useEffect, useState, useCallback } from "react";
import { adminApi, TOKEN_KEY, API } from "./adminApi";
import { ConfirmDeleteModal } from "./PapersPanel";
import {
  CalendarCheck,
  RefreshCw,
  Search,
  FileSpreadsheet,
  Loader2,
  Trash2,
  Users,
} from "lucide-react";

const AIWeekPanel = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRegistrations = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = { limit: 5000 };
      if (q && q.trim()) params.search = q.trim();
      const res = await adminApi.get("/admin/aiweek2026/registrations", { params });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchRegistrations(search), 300);
    return () => clearTimeout(t);
  }, [search, fetchRegistrations]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeletingId(pendingDelete.id);
    try {
      await adminApi.delete(`/admin/aiweek2026/registrations/${pendingDelete.id}`);
      setPendingDelete(null);
      fetchRegistrations(search);
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/aiweek2026/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          window.location.reload();
          return;
        }
        throw new Error("Export failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aiweek2026-registrations-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not export. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const regs = data?.registrations || [];

  return (
    <div className="space-y-6" data-testid="admin-aiweek-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-light text-gray-900 tracking-tight">
            AI Week 2026
            <span className="ml-3 text-sm text-gray-400 font-mono">
              {data?.total ?? 0}
            </span>
          </h2>
          <p className="text-sm text-gray-500 font-light mt-1">
            Conference registrations for AI Week 2026.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchRegistrations(search)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-gray-200 rounded-md hover:border-gray-900 transition-colors"
            data-testid="aiweek-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || !regs.length}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50"
            data-testid="aiweek-export"
          >
            {exporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-3.5 h-3.5" />
            )}
            Export to Excel
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, affiliation, country…"
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 transition-colors"
          data-testid="aiweek-search"
        />
      </div>

      {regs.length === 0 ? (
        <div className="text-center py-20 bg-white border border-dashed border-gray-200 rounded-xl">
          <CalendarCheck className="w-8 h-8 text-gray-300 mx-auto mb-4" />
          <p className="text-sm text-gray-500 font-light">
            {search ? "No registrations match your search." : "No registrations yet."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500">
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Affiliation</th>
                  <th className="px-5 py-3">Country</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Phone</th>
                  <th className="px-5 py-3">Registered</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {regs.map((r, i) => (
                  <tr key={r.id || i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-gray-400 font-mono text-xs">
                      {String(i + 1).padStart(2, "0")}
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-900 whitespace-nowrap">
                      {r.name}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      <a href={`mailto:${r.email}`} className="hover:underline text-[#00BCD4]">
                        {r.email}
                      </a>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{r.affiliation}</td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{r.country}</td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{r.role}</td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                      {r.phone || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-gray-400 font-mono text-xs whitespace-nowrap">
                      {new Date(r.timestamp).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => setPendingDelete(r)}
                        disabled={deletingId === r.id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                        aria-label="Delete registration"
                        data-testid={`aiweek-delete-${r.id}`}
                      >
                        {deletingId === r.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendingDelete && (
        <ConfirmDeleteModal
          label="registration"
          preview={`${pendingDelete.name} · ${pendingDelete.email}`}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          loading={deletingId === pendingDelete.id}
        />
      )}
    </div>
  );
};

export default AIWeekPanel;
